import { useRef, useCallback } from "react";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivityFeed, flattenActivityPages } from "@/hooks/useActivityFeed";
import { ActivityItem } from "@/components/ActivityItem";
import type { ActivityFeedFilters } from "@/hooks/useActivityFeed";
import type { ActivityEntityType } from "@/lib/activityLogger";

interface ActivityFeedProps {
  orgId: string | null;
  /** Initial filters; parent can override */
  filters?: ActivityFeedFilters;
  /** Show compact variant (no avatar, smaller text) */
  compact?: boolean;
  /** Max items to show (no infinite scroll) */
  limit?: number;
  /** Show the filter toolbar */
  showFilters?: boolean;
  /** Custom empty message */
  emptyMessage?: string;
  className?: string;
}

const ENTITY_TYPE_OPTIONS: { value: ActivityEntityType | "all"; label: string }[] = [
  { value: "all",              label: "All activity" },
  { value: "booking",          label: "Bookings" },
  { value: "host",             label: "Hosts" },
  { value: "unit",             label: "Units" },
  { value: "document",         label: "Documents" },
  { value: "dispute",          label: "Disputes" },
  { value: "compliance_action",label: "Compliance" },
];

export function ActivityFeed({
  orgId,
  filters = {},
  compact = false,
  limit,
  showFilters = false,
  emptyMessage = "No activity yet.",
  className = "",
}: ActivityFeedProps) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useActivityFeed(orgId, filters);

  const entries = flattenActivityPages(data?.pages);
  const displayEntries = limit ? entries.slice(0, limit) : entries;

  // Intersection observer for infinite scroll
  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loaderCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node || !hasNextPage || limit) return;
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, limit]
  );

  if (isLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        {[...Array(compact ? 4 : 5)].map((_, i) => (
          <Skeleton key={i} className={compact ? "h-8 w-full" : "h-14 w-full"} />
        ))}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {showFilters && (
        <div className="flex items-center gap-2 mb-3">
          <Select
            defaultValue="all"
            onValueChange={() => {/* parent controls filters */}}
          >
            <SelectTrigger className="h-8 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {displayEntries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
          <Activity className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      ) : (
        <div>
          {displayEntries.map((entry) => (
            <ActivityItem key={entry.id} entry={entry} compact={compact} />
          ))}

          {/* Infinite scroll loader */}
          {!limit && (
            <div ref={loaderCallback}>
              {isFetchingNextPage && (
                <div className="flex justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!hasNextPage && entries.length > 0 && (
                <p className="text-xs text-center text-muted-foreground py-3">
                  All activity loaded
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
